import { useMinuteTicker } from '@/hooks/useMinuteTicker'
import { useNewsListQuery } from '@/queries/home/homeQueries'
import { NewsArticle } from '@components/organism/common/NewsArticle.jsx'
import { SectionTitle } from '@components/organism/common/SectionTitle.jsx'
import { DEFAULT_USE_TYPE, USE_TYPE } from '@constants/appTarget.js'
import { usePolling } from '@hooks/usePolling.js'
import { useOrgInfoStore } from '@store/common/useOrgInfoStore.js'
import { formatTimeStringToDate } from '@utils/formatTime.js'
import { useTranslation } from 'react-i18next'
import { MdKeyboardDoubleArrowRight } from 'react-icons/md'
import { Link } from 'react-router-dom'
import { ErrorUI } from '@components/organism/common/ErrorUI.jsx'
import { LoadingCom } from '@components/organism/common/LoadingCom.jsx'

const NEWS_SECTION_MODE = {
  PAGE: 'page',
  WIDGET: 'widget'
}

const MORE_LINK_POSITION = {
  HEADER: 'header',
  FOOTER: 'footer'
}

const NEWS_SECTION_CONFIG = {
  [NEWS_SECTION_MODE.PAGE]: {
    listPath: '/news',
    detailPath: (key) => `/news/${key}`,
    detailState: undefined,
    showTitleTime: true,
    moreLinkPosition: MORE_LINK_POSITION.FOOTER,
    headerClassName: ''
  },
  [NEWS_SECTION_MODE.WIDGET]: {
    listPath: '/widgets/news/list',
    detailPath: (key) => `/widgets/news/${key}`,
    detailState: { from: '/widgets/news' },
    showTitleTime: false,
    moreLinkPosition: MORE_LINK_POSITION.HEADER,
    headerClassName: 'flex items-center justify-between'
  }
}

const VARIANT_CONFIG = {
  [USE_TYPE.KBPAY]: {
    newsRefreshMinutes: null,
    useDateTime: true,
    sectionTitleTo: undefined,
    showFooterMoreLink: true,
    useLastBorder: true,

    Error: null,
    Loading: null
  },
  [USE_TYPE.jb]: {
    newsRefreshMinutes: 5,
    useDateTime: false,
    sectionTitleTo: '/news',
    showFooterMoreLink: false,
    useLastBorder: false,

    Error: ({ onRetry }) => <ErrorUI height="668" handleClick={onRetry} />,
    Loading: () => <LoadingCom height="668" />
  }
}

const NewsMoreLink = ({ mode, to }) => {
  const { t } = useTranslation()
  if (mode === NEWS_SECTION_MODE.WIDGET) {
    return (
      <Link to={to} className="flex items-center gap-1">
        {t('common.more')}
        <MdKeyboardDoubleArrowRight size={20} />
      </Link>
    )
  }

  return (
    <Link
      to={to}
      className={
        'border-navy-300 text-navy-900 text-16 mt-24 block w-full rounded-t-lg rounded-r-lg border py-14 text-center font-bold'
      }
    >
      {t('common.more')}
    </Link>
  )
}

const NewsSection = ({ isWidget = false, variant = 'v1' }) => {
  const { t } = useTranslation()
  const { data, refetch, isError, isLoading } = useNewsListQuery({ page: 1, count: 5 })
  useMinuteTicker({ enabled: variant === 'v2' })
  const currentTime = new Date()

  const mode = isWidget ? NEWS_SECTION_MODE.WIDGET : NEWS_SECTION_MODE.PAGE
  const config = NEWS_SECTION_CONFIG[mode]
  const moreLink = <NewsMoreLink mode={mode} to={config.listPath} />

  const useType = useOrgInfoStore((s) => s.useType)
  const {
    newsRefreshMinutes,
    useDateTime,
    sectionTitleTo,
    showFooterMoreLink,
    useLastBorder,
    Error: ErrorFallback,
    Loading: LoadingFallback
  } = VARIANT_CONFIG[useType] ?? VARIANT_CONFIG[DEFAULT_USE_TYPE]

  const titleTime =
    config.showTitleTime && !isLoading
      ? useDateTime
        ? (formatTimeStringToDate(data?.lastFetchTime) ?? '')
        : ''
      : ''

  const autoRefresh = !isWidget && newsRefreshMinutes != null

  usePolling(refetch, `${newsRefreshMinutes ?? 5}m-20s`, { enabled: autoRefresh })

  return (
    <section className={'x-spacing y-spacing bg-white'}>
      <div className={config.headerClassName}>
        <SectionTitle
          title={t(isWidget ? 'widget.news' : 'home.news.title')}
          time={titleTime}
          to={isWidget ? undefined : sectionTitleTo}
        />
        {config.moreLinkPosition === MORE_LINK_POSITION.HEADER && moreLink}
      </div>
      {isLoading && LoadingFallback ? (
        <LoadingFallback />
      ) : isError && ErrorFallback ? (
        <ErrorFallback onRetry={refetch} />
      ) : (
        <>
          <section className={'pt-24'}>
            {Object.entries(data?.newsVOS || {})?.map(
              ([_, { key, pressNm, publishedDt, title, thumbnailLnk }]) => (
                <Link
                  to={config.detailPath(key)}
                  key={key}
                  state={config.detailState}
                  className={useLastBorder ? '' : 'last:[&>article]:border-b-0'}
                >
                  <NewsArticle
                    title={title}
                    pressNm={pressNm}
                    thumbnailLnk={thumbnailLnk}
                    publishedDt={publishedDt}
                    variant={variant}
                    currentTime={currentTime}
                  />
                </Link>
              )
            )}
          </section>

          {showFooterMoreLink && config.moreLinkPosition === MORE_LINK_POSITION.FOOTER && moreLink}
        </>
      )}
    </section>
  )
}

export default NewsSection
